import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { baseUrl } from "../config";

// Types for API responses
export interface ApiResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  ok: boolean;
}

// URLs that don't require access token
const NoAccessTokenURL = ["user/login/", "user/verify-otp/", "user/register/"];

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: baseUrl + "/api/",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

let isRefreshing = false;
let refreshPromise: Promise<string | null> | undefined = undefined;

interface ResData {
  access: string;
  refresh: string;
}

// Store references to auth functions
let authFunctions: {
  logOut: () => void;
  updateTokens: (token: string, refresh: string) => void;
  getToken: () => string | null;
  getRefreshToken: () => string | null;
} | null = null;

export const configureClient = (authStore: {
  logOut: () => void;
  updateTokens: (token: string, refresh: string) => void;
  getToken: () => string | null;
  getRefreshToken: () => string | null;
}) => {
  authFunctions = authStore;
};

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Check if this URL needs access token
    let accessTokenNeeded = true;
    NoAccessTokenURL.forEach((url) => {
      if (config.url === url) {
        accessTokenNeeded = false;
      }
    });

    // Add auth token if needed and available
    if (accessTokenNeeded && authFunctions) {
      const token = authFunctions.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Set content type based on data
    if (config.data instanceof FormData) {
      config.headers["Content-Type"] = "multipart/form-data";
    } else {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      authFunctions
    ) {
      const refreshToken = authFunctions.getRefreshToken();

      if (refreshToken) {
        originalRequest._retry = true;

        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = refreshTokenFn(refreshToken);

          try {
            const newToken = await refreshPromise;
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return apiClient(originalRequest);
            }
          } finally {
            isRefreshing = false;
            refreshPromise = undefined;
          }
        } else if (refreshPromise) {
          const newToken = await refreshPromise;
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          }
        }
      } else {
        // No refresh token available, log out
        // authFunctions.logOut();
      }
    }

    return Promise.reject(error);
  }
);

const refreshTokenFn = async (refreshToken: string): Promise<string | null> => {
  try {
    const response = await axios.post(`${baseUrl}/api/user/token/refresh/`, {
      refresh: refreshToken,
    });

    const resData: ResData = response.data;

    if (response.status === 200 && resData?.access && authFunctions) {
      const newToken = resData.access;
      const newRefresh = resData.refresh;
      authFunctions.updateTokens(newToken, newRefresh);
      console.log("Token refreshed successfully");
      return newToken;
    } else {
      console.log("Failed to refresh token:", response.status);
      if (authFunctions) {
        // authFunctions.logOut();
      }
      return null;
    }
  } catch (error) {
    console.error("Error refreshing token:", error);
    if (authFunctions) {
      // authFunctions.logOut();
    }
    return null;
  }
};

// Wrapper functions to match mobile API structure
export const get = async <T>(
  url: string,
  params?: any,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
  try {
    const response: AxiosResponse<T> = await apiClient.get(url, {
      params,
      ...config,
    });
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      ok: response.status >= 200 && response.status < 300,
    };
  } catch (error: any) {
    return {
      data: error.response?.data,
      status: error.response?.status || 0,
      statusText: error.response?.statusText || "Network Error",
      ok: false,
    };
  }
};

export const post = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
  try {
    const response: AxiosResponse<T> = await apiClient.post(url, data, config);
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      ok: response.status >= 200 && response.status < 300,
    };
  } catch (error: any) {
    return {
      data: error.response?.data,
      status: error.response?.status || 0,
      statusText: error.response?.statusText || "Network Error",
      ok: false,
    };
  }
};

export const put = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
  try {
    const response: AxiosResponse<T> = await apiClient.put(url, data, config);
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      ok: response.status >= 200 && response.status < 300,
    };
  } catch (error: any) {
    return {
      data: error.response?.data,
      status: error.response?.status || 0,
      statusText: error.response?.statusText || "Network Error",
      ok: false,
    };
  }
};

export const patch = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
  try {
    const response: AxiosResponse<T> = await apiClient.patch(url, data, config);
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      ok: response.status >= 200 && response.status < 300,
    };
  } catch (error: any) {
    return {
      data: error.response?.data,
      status: error.response?.status || 0,
      statusText: error.response?.statusText || "Network Error",
      ok: false,
    };
  }
};

export const del = async <T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
  try {
    const response: AxiosResponse<T> = await apiClient.delete(url, config);
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      ok: response.status >= 200 && response.status < 300,
    };
  } catch (error: any) {
    return {
      data: error.response?.data,
      status: error.response?.status || 0,
      statusText: error.response?.statusText || "Network Error",
      ok: false,
    };
  }
};

export default apiClient;
